import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { PageSEO } from '../components/SEO';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { RestaurantCard } from '../components/RestaurantCard';
import { SearchBar } from '../components/SearchBar';
import { calculateDistance } from '../components/AddressAutocomplete';
import { checkIsOpen } from '../utils/openingHours';
import { 
  Search, 
  MapPin, 
  Clock, 
  Star, 
  X,
  SlidersHorizontal,
  Navigation,
  DollarSign,
  ChevronDown,
  TrendingUp,
  ChevronUp
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '../components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const cuisineTypes = [
  'Burger', 'Sushi', 'Japonais', 'Pizza', 'Salade bar', 'Healthy', 
  'Américain', 'Français', 'Italien', 'Asiatique', 'Mexicain'
];

const priceRanges = [
  { value: 'all', label: 'Tous les prix', icon: '💰' },
  { value: '1', label: '€ - Économique', max: 10 },
  { value: '2', label: '€€ - Moyen', max: 20 },
  { value: '3', label: '€€€ - Premium', max: 100 },
];

const distanceOptions = [
  { value: 'all', label: 'Toutes distances' },
  { value: '1', label: '< 1 km' },
  { value: '3', label: '< 3 km' },
  { value: '5', label: '< 5 km' },
  { value: '10', label: '< 10 km' },
];

const ratingOptions = [
  { value: 'all', label: 'Toutes notes' },
  { value: '4', label: '4+ étoiles' },
  { value: '3', label: '3+ étoiles' },
];

const sortOptions = [
  { value: 'relevance', label: 'Pertinence' },
  { value: 'distance', label: 'Distance' },
  { value: 'rating', label: 'Meilleure note' },
  { value: 'popularity', label: 'Popularité' },
];

export default function RestaurantList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [cityFilter, setCityFilter] = useState(searchParams.get('city') || '');
  const [selectedCuisines, setSelectedCuisines] = useState(
    searchParams.get('cuisine') ? [searchParams.get('cuisine')] : []
  );
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState('pending'); // pending, granted, denied, unavailable
  const [isScrolled, setIsScrolled] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  
  // Advanced filters
  const [priceFilter, setPriceFilter] = useState(searchParams.get('price') || 'all');
  const [distanceFilter, setDistanceFilter] = useState(searchParams.get('distance') || 'all');
  const [ratingFilter, setRatingFilter] = useState(searchParams.get('rating') || 'all');
  const [openNowFilter, setOpenNowFilter] = useState(searchParams.get('open') === 'true');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'relevance');
  // Filters managed by Sheet component

  // Track scroll for compact header
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 100);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Get user location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocationStatus('granted');
        },
        (error) => {
          console.log('Geolocation error:', error.code);
          if (error.code === 1) {
            setLocationStatus('denied');
          } else {
            setLocationStatus('unavailable');
          }
        }
      );
    } else {
      setLocationStatus('unavailable');
    }
  }, []);

  // Function to request location again
  const requestLocation = () => {
    setLocationStatus('pending');
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocationStatus('granted');
        },
        (error) => {
          if (error.code === 1) {
            setLocationStatus('denied');
          } else {
            setLocationStatus('unavailable');
          }
        }
      );
    }
  };

  const fetchRestaurants = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchParams.get('search')) params.append('search', searchParams.get('search'));
      if (searchParams.get('city')) params.append('city', searchParams.get('city'));
      if (searchParams.get('cuisine')) params.append('cuisine_type', searchParams.get('cuisine'));
      
      const response = await axios.get(`${API}/restaurants?${params.toString()}`);
      setRestaurants(response.data);
    } catch {
      console.error('Error fetching restaurants');
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  // Check if restaurant is open now - uses API status first, then fallback to hours check
  const isRestaurantOpen = (restaurant) => {
    // First check if API provides is_currently_open (takes into account manual close + hours)
    if (restaurant.is_currently_open !== undefined) {
      return restaurant.is_currently_open;
    }
    // Fallback to local hours check
    return checkIsOpen(restaurant.opening_hours).isOpen;
  };

  // Calculate distance for a restaurant
  const getRestaurantDistance = useCallback((restaurant) => {
    if (!userLocation || !restaurant.latitude || !restaurant.longitude) return null;
    return calculateDistance(
      userLocation.lat,
      userLocation.lng,
      restaurant.latitude,
      restaurant.longitude
    );
  }, [userLocation]);

  // Filter and sort restaurants
  const filteredRestaurants = useMemo(() => {
    let result = [...restaurants];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(r => 
        r.name.toLowerCase().includes(query) ||
        r.cuisine_type?.some(c => c.toLowerCase().includes(query))
      );
    }

    // Filter by city
    if (cityFilter) {
      const city = cityFilter.toLowerCase();
      result = result.filter(r => r.city?.toLowerCase().includes(city));
    }

    // Filter by cuisine
    if (selectedCuisines.length > 0) {
      result = result.filter(r => 
        r.cuisine_type?.some(c => 
          selectedCuisines.some(sc => c.toLowerCase().includes(sc.toLowerCase()))
        )
      );
    }

    // Filter by price range (based on average dish price - simulated)
    if (priceFilter !== 'all') {
      const priceLevel = parseInt(priceFilter);
      result = result.filter(r => {
        // Simulate price level based on restaurant name/type
        const simulatedPrice = (r.name.length % 3) + 1;
        return simulatedPrice === priceLevel;
      });
    }

    // Filter by distance
    if (distanceFilter !== 'all' && userLocation) {
      const maxDistance = parseInt(distanceFilter);
      result = result.filter(r => {
        const distance = getRestaurantDistance(r);
        return distance !== null && distance <= maxDistance;
      });
    }

    // Filter by rating
    if (ratingFilter !== 'all') {
      const minRating = parseInt(ratingFilter);
      result = result.filter(r => (r.avg_rating || 0) >= minRating);
    }

    // Filter by open now
    if (openNowFilter) {
      result = result.filter(r => isRestaurantOpen(r));
    }

    // Sort
    switch (sortBy) {
      case 'distance':
        result.sort((a, b) => {
          const distA = getRestaurantDistance(a) || 999;
          const distB = getRestaurantDistance(b) || 999;
          return distA - distB;
        });
        break;
      case 'rating':
        result.sort((a, b) => (b.avg_rating || 0) - (a.avg_rating || 0));
        break;
      case 'popularity':
        result.sort((a, b) => (b.total_reviews || 0) - (a.total_reviews || 0));
        break;
      default:
        // relevance - keep original order
        break;
    }

    return result;
  }, [restaurants, searchQuery, cityFilter, selectedCuisines, priceFilter, distanceFilter, ratingFilter, openNowFilter, sortBy, userLocation, getRestaurantDistance]);

  // Extract all unique cuisines from restaurants
  const allCuisines = useMemo(() => {
    const cuisines = new Set();
    restaurants.forEach(r => {
      r.cuisine_type?.forEach(c => cuisines.add(c));
    });
    return Array.from(cuisines).sort();
  }, [restaurants]);

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    applyFilters();
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (cityFilter) params.set('city', cityFilter);
    if (selectedCuisines.length > 0) params.set('cuisine', selectedCuisines[0]);
    if (priceFilter !== 'all') params.set('price', priceFilter);
    if (distanceFilter !== 'all') params.set('distance', distanceFilter);
    if (ratingFilter !== 'all') params.set('rating', ratingFilter);
    if (openNowFilter) params.set('open', 'true');
    if (sortBy !== 'relevance') params.set('sort', sortBy);
    setSearchParams(params);
  };

  const handleCuisineFilter = (cuisine) => {
    if (selectedCuisines.includes(cuisine)) {
      setSelectedCuisines(selectedCuisines.filter(c => c !== cuisine));
    } else {
      setSelectedCuisines([cuisine]);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setCityFilter('');
    setSelectedCuisines([]);
    setPriceFilter('all');
    setDistanceFilter('all');
    setRatingFilter('all');
    setOpenNowFilter(false);
    setSortBy('relevance');
    setSearchParams({});
  };

  const activeFiltersCount = [
    searchQuery,
    cityFilter,
    selectedCuisines.length > 0,
    priceFilter !== 'all',
    distanceFilter !== 'all',
    ratingFilter !== 'all',
    openNowFilter,
  ].filter(Boolean).length;

  const hasActiveFilters = activeFiltersCount > 0;

  const FilterPanel = ({ mobile = false }) => (
    <div className={`space-y-6 ${mobile ? '' : 'p-4 bg-white rounded-2xl border border-gray-200'}`}>
      {/* Price Filter */}
      <div>
        <Label className="text-sm font-semibold text-gray-700 mb-3 block">
          <DollarSign className="w-4 h-4 inline mr-1" />
          Gamme de prix
        </Label>
        <div className="space-y-2">
          {priceRanges.map((range) => (
            <label 
              key={range.value}
              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                priceFilter === range.value 
                  ? 'bg-emerald-50 border-emerald-200 border' 
                  : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              <input
                type="radio"
                name="price"
                value={range.value}
                checked={priceFilter === range.value}
                onChange={(e) => setPriceFilter(e.target.value)}
                className="text-emerald-500 focus:ring-emerald-500"
              />
              <span className="text-sm">{range.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Distance Filter - Always show, with location status */}
      <div>
        <Label className="text-sm font-semibold text-gray-700 mb-3 block">
          <Navigation className="w-4 h-4 inline mr-1" />
          Distance
        </Label>
        
        {/* Location status messages */}
        {locationStatus === 'pending' && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl mb-3">
            <p className="text-sm text-blue-700 flex items-center gap-2">
              <span className="animate-pulse">📍</span>
              Recherche de votre position...
            </p>
          </div>
        )}
        
        {locationStatus === 'denied' && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl mb-3">
            <p className="text-sm text-amber-700 mb-2">
              📍 La localisation est désactivée
            </p>
            <button
              onClick={requestLocation}
              className="text-xs text-amber-800 underline hover:no-underline"
            >
              Cliquez ici pour autoriser la géolocalisation
            </button>
          </div>
        )}
        
        {locationStatus === 'unavailable' && (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl mb-3">
            <p className="text-sm text-gray-600">
              📍 Géolocalisation non disponible sur votre appareil
            </p>
          </div>
        )}
        
        {locationStatus === 'granted' && userLocation && (
          <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-xl mb-3">
            <p className="text-xs text-emerald-700 flex items-center gap-1">
              <span>✓</span> Position détectée
            </p>
          </div>
        )}
        
        {/* Distance options - only enabled if location is granted */}
        <div className="space-y-2">
          {distanceOptions.map((option) => (
            <label 
              key={option.value}
              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                !userLocation ? 'opacity-50 cursor-not-allowed' : ''
              } ${
                distanceFilter === option.value && userLocation
                  ? 'bg-emerald-50 border-emerald-200 border' 
                  : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              <input
                type="radio"
                name="distance"
                value={option.value}
                checked={distanceFilter === option.value}
                onChange={(e) => userLocation && setDistanceFilter(e.target.value)}
                disabled={!userLocation}
                className="text-emerald-500 focus:ring-emerald-500"
              />
              <span className="text-sm">{option.label}</span>
            </label>
          ))}
        </div>
      </div>
      )}

      {/* Rating Filter */}
      <div>
        <Label className="text-sm font-semibold text-gray-700 mb-3 block">
          <Star className="w-4 h-4 inline mr-1" />
          Note minimum
        </Label>
        <div className="space-y-2">
          {ratingOptions.map((option) => (
            <label 
              key={option.value}
              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                ratingFilter === option.value 
                  ? 'bg-emerald-50 border-emerald-200 border' 
                  : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              <input
                type="radio"
                name="rating"
                value={option.value}
                checked={ratingFilter === option.value}
                onChange={(e) => setRatingFilter(e.target.value)}
                className="text-emerald-500 focus:ring-emerald-500"
              />
              <span className="text-sm flex items-center gap-1">
                {option.label}
                {option.value !== 'all' && (
                  <span className="flex">
                    {[...Array(parseInt(option.value))].map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    ))}
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Open Now Filter */}
      <div>
        <label className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
          openNowFilter 
            ? 'bg-emerald-50 border-emerald-200 border' 
            : 'hover:bg-gray-50 border border-transparent'
        }`}>
          <Checkbox
            checked={openNowFilter}
            onCheckedChange={setOpenNowFilter}
          />
          <span className="text-sm font-medium">
            <Clock className="w-4 h-4 inline mr-1" />
            Ouvert maintenant
          </span>
        </label>
      </div>

      {/* Cuisine Types */}
      <div>
        <Label className="text-sm font-semibold text-gray-700 mb-3 block">
          Type de cuisine
        </Label>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {cuisineTypes.map((cuisine) => (
            <label 
              key={cuisine}
              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                selectedCuisines.includes(cuisine) 
                  ? 'bg-emerald-50 border-emerald-200 border' 
                  : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              <Checkbox
                checked={selectedCuisines.includes(cuisine)}
                onCheckedChange={() => handleCuisineFilter(cuisine)}
              />
              <span className="text-sm">{cuisine}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Apply Button (Mobile) */}
      {mobile && (
        <SheetClose asChild>
          <Button 
            onClick={applyFilters}
            className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-xl"
          >
            Appliquer les filtres
          </Button>
        </SheetClose>
      )}
    </div>
  );

  return (
    <Layout>
      <PageSEO.Restaurants />
      <div className="bg-gray-50 min-h-screen">
        {/* Header - Compact on scroll */}
        <div className={`bg-white border-b border-gray-200 sticky top-16 z-30 transition-all duration-300 ${isScrolled ? 'shadow-md' : ''}`}>
          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-300 ${isScrolled ? 'py-2' : 'py-4'}`}>
            {/* Search Form - Hide on scroll, show toggle */}
            {!isScrolled ? (
              <SearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                cityFilter={cityFilter}
                setCityFilter={setCityFilter}
                onSearch={handleSearch}
                restaurants={restaurants}
                cuisineTypes={allCuisines}
              />
            ) : (
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-1">
                  <div className="flex-1 max-w-md">
                    <Input
                      type="text"
                      placeholder="Rechercher..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-9 rounded-lg"
                    />
                  </div>
                  <span className="text-sm text-gray-500 hidden sm:inline">
                    {filteredRestaurants.length} résultat{filteredRestaurants.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-emerald-600"
                >
                  {showFilters ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                  Filtres
                </Button>
              </div>
            )}
              
              {/* Mobile Filter Button */}
              {(!isScrolled || showFilters) && (
              <div className="flex sm:hidden mt-3">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="h-12 w-full rounded-xl relative">
                    <SlidersHorizontal className="w-5 h-5 mr-2" />
                    Filtres
                    {activeFiltersCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 text-white text-xs rounded-full flex items-center justify-center">
                        {activeFiltersCount}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:w-96 overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle className="flex items-center justify-between">
                      Filtres
                      {hasActiveFilters && (
                        <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-500">
                          Effacer tout
                        </Button>
                      )}
                    </SheetTitle>
                  </SheetHeader>
                  <div className="py-4">
                    <FilterPanel mobile />
                  </div>
                </SheetContent>
              </Sheet>
            </div>
            )}

            {/* Quick Filters Bar - Compact on scroll */}
            {(!isScrolled || showFilters) && (
            <div className={`flex items-center gap-2 flex-wrap transition-all duration-300 ${isScrolled ? 'mt-2' : 'mt-4'}`}>
              {/* Sort Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-full gap-1">
                    <TrendingUp className="w-4 h-4" />
                    {sortOptions.find(s => s.value === sortBy)?.label}
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {sortOptions.map((option) => (
                    <DropdownMenuItem 
                      key={option.value}
                      onClick={() => setSortBy(option.value)}
                      className={sortBy === option.value ? 'bg-emerald-50' : ''}
                    >
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Quick Filter Buttons */}
              <Button 
                variant={openNowFilter ? "default" : "outline"} 
                size="sm" 
                className={`rounded-full ${openNowFilter ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
                onClick={() => setOpenNowFilter(!openNowFilter)}
              >
                <Clock className="w-4 h-4 mr-1" />
                Ouvert
              </Button>

              <Button 
                variant={ratingFilter === '4' ? "default" : "outline"} 
                size="sm" 
                className={`rounded-full ${ratingFilter === '4' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
                onClick={() => setRatingFilter(ratingFilter === '4' ? 'all' : '4')}
              >
                <Star className="w-4 h-4 mr-1 fill-yellow-400 text-yellow-400" />
                4+ étoiles
              </Button>

              {userLocation && (
                <Button 
                  variant={distanceFilter === '3' ? "default" : "outline"} 
                  size="sm" 
                  className={`rounded-full ${distanceFilter === '3' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
                  onClick={() => setDistanceFilter(distanceFilter === '3' ? 'all' : '3')}
                >
                  <Navigation className="w-4 h-4 mr-1" />
                  &lt; 3 km
                </Button>
              )}

              {/* Cuisine Quick Filters */}
              <div className="hidden sm:flex items-center gap-2">
                {cuisineTypes.slice(0, 5).map((cuisine) => (
                  <button
                    key={cuisine}
                    onClick={() => handleCuisineFilter(cuisine)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      selectedCuisines.includes(cuisine)
                        ? 'bg-emerald-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {cuisine}
                  </button>
                ))}
              </div>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-1 rounded-full text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Effacer
                </button>
              )}
            </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex gap-8">
            {/* Desktop Sidebar Filters */}
            <aside className="hidden lg:block w-72 shrink-0">
              <div className="sticky top-40">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-gray-900">Filtres</h2>
                  {hasActiveFilters && (
                    <button onClick={clearFilters} className="text-sm text-red-500 hover:underline">
                      Effacer tout
                    </button>
                  )}
                </div>
                <FilterPanel />
              </div>
            </aside>

            {/* Results */}
            <div className="flex-1">
              {/* Active Filters */}
              {hasActiveFilters && (
                <div className="flex items-center gap-2 mb-6 flex-wrap">
                  <span className="text-sm text-gray-500">Filtres actifs:</span>
                  {searchQuery && (
                    <Badge variant="secondary" className="gap-1 rounded-full">
                      &quot;{searchQuery}&quot;
                      <button onClick={() => setSearchQuery('')}>
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  {cityFilter && (
                    <Badge variant="secondary" className="gap-1 rounded-full">
                      📍 {cityFilter}
                      <button onClick={() => setCityFilter('')}>
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  {selectedCuisines.map((cuisine) => (
                    <Badge key={cuisine} variant="secondary" className="gap-1 rounded-full">
                      {cuisine}
                      <button onClick={() => handleCuisineFilter(cuisine)}>
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                  {priceFilter !== 'all' && (
                    <Badge variant="secondary" className="gap-1 rounded-full">
                      {priceRanges.find(p => p.value === priceFilter)?.label}
                      <button onClick={() => setPriceFilter('all')}>
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  {distanceFilter !== 'all' && (
                    <Badge variant="secondary" className="gap-1 rounded-full">
                      &lt; {distanceFilter} km
                      <button onClick={() => setDistanceFilter('all')}>
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  {ratingFilter !== 'all' && (
                    <Badge variant="secondary" className="gap-1 rounded-full">
                      {ratingFilter}+ ⭐
                      <button onClick={() => setRatingFilter('all')}>
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                  {openNowFilter && (
                    <Badge variant="secondary" className="gap-1 rounded-full">
                      Ouvert maintenant
                      <button onClick={() => setOpenNowFilter(false)}>
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  )}
                </div>
              )}

              {/* Results Count */}
              <p className="text-gray-600 mb-6">
                {loading ? 'Chargement...' : `${filteredRestaurants.length} restaurant${filteredRestaurants.length !== 1 ? 's' : ''} trouvé${filteredRestaurants.length !== 1 ? 's' : ''}`}
              </p>

              {/* Restaurant Grid */}
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-white rounded-2xl p-4 animate-pulse">
                      <div className="aspect-[4/3] bg-gray-200 rounded-xl mb-4" />
                      <div className="h-5 bg-gray-200 rounded mb-2" />
                      <div className="h-4 bg-gray-200 rounded w-2/3 mb-3" />
                      <div className="h-4 bg-gray-200 rounded w-1/2" />
                    </div>
                  ))}
                </div>
              ) : filteredRestaurants.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <Search className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun restaurant trouvé</h3>
                  <p className="text-gray-500 mb-6">Essayez de modifier vos critères de recherche</p>
                  <Button onClick={clearFilters} variant="outline" className="rounded-full">
                    Effacer les filtres
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredRestaurants.map((restaurant, index) => {
                    const distance = getRestaurantDistance(restaurant);
                    const isOpen = isRestaurantOpen(restaurant);
                    
                    return (
                      <RestaurantCard
                        key={restaurant.id}
                        restaurant={restaurant}
                        distance={distance}
                        isOpen={isOpen}
                        animationDelay={index * 0.05}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
